import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, TextAa, ArrowsClockwise, GitDiff, Check, X, PencilSimple,
  FloppyDisk, PencilLine, CircleNotch, Warning, ListBullets, Article, ChatText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { chapterApi, suggestionApi, analysisApi } from "@/api";
import { Brand, ThemeToggle } from "@/components/Brand";

const SEV = {
  erreur: { color: "#DC2626", label: "erreur", dot: "🔴" },
  suggestion: { color: "#F97316", label: "suggestion", dot: "🟠" },
  repetition: { color: "#EAB308", label: "répétition", dot: "🟡" },
};
const TEXT_SIZES = ["text-base", "text-lg", "text-xl"];

// Coerce any value to a safe display string (prevents "objects are not valid as a React child")
const T = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(T).join(" ");
  if (typeof v === "object") return Object.entries(v).map(([k, val]) => `${k}: ${T(val)}`).join(" ");
  return String(v);
};

export default function Editor() {
  const { pid, cid } = useParams();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [current, setCurrent] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [inconsistencies, setInconsistencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [mobileView, setMobileView] = useState("text"); // chapters | text | analysis
  const [sizeIdx, setSizeIdx] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState("");
  const [active, setActive] = useState(null);
  const [modifyId, setModifyId] = useState(null);
  const [modifyText, setModifyText] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [compare, setCompare] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const centerRef = useRef(null);
  const highlightRef = useRef(null);
  const pollRef = useRef(null);

  const loadChapters = useCallback(() => chapterApi.list(pid).then(setChapters), [pid]);

  const loadChapter = useCallback(async (chapterId) => {
    setLoading(true);
    try {
      const data = await chapterApi.get(pid, chapterId);
      setCurrent(data.chapter);
      setDraft(data.chapter.working_text);
      setSuggestions(data.suggestions);
      setInconsistencies(data.inconsistencies);
      setActive(null);
      setEditMode(false);
      if (centerRef.current) centerRef.current.scrollTop = 0;
    } finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { loadChapters(); }, [loadChapters]);

  useEffect(() => {
    if (cid) loadChapter(cid);
    else chapterApi.list(pid).then((chs) => { if (chs[0]) navigate(`/project/${pid}/editor/${chs[0].id}`, { replace: true }); });
  }, [cid, pid, loadChapter, navigate]);

  // scroll to active highlight
  useEffect(() => {
    if (active && highlightRef.current) highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [active]);

  const counts = useMemo(() => {
    const pend = suggestions.filter((s) => s.status === "pending");
    return {
      erreur: pend.filter((s) => s.severity === "erreur").length,
      suggestion: pend.filter((s) => s.severity === "suggestion").length,
      repetition: pend.filter((s) => s.type === "repetition").length,
      incoherence: inconsistencies.filter((i) => i.status === "pending").length,
    };
  }, [suggestions, inconsistencies]);

  const filtered = useMemo(() => {
    let list = suggestions.filter((s) => s.status === "pending");
    if (filter === "all") return list;
    if (filter === "erreur") return list.filter((s) => s.severity === "erreur");
    if (filter === "suggestion") return list.filter((s) => s.severity === "suggestion");
    if (filter === "repetition") return list.filter((s) => s.type === "repetition");
    return list.filter((s) => s.type === filter);
  }, [suggestions, filter]);

  const act = async (s, action, newText) => {
    if (!current?.id) return;
    try {
      const res = await suggestionApi.action(pid, s.id, { action, new_text: newText });
      if ((action === "accept" || action === "modify") && res && res.applied === false) {
        toast.warning("Le passage exact n'a pas été retrouvé dans le texte. Utilisez « Modifier » ou éditez le chapitre manuellement.");
        return; // keep the card so the user can act
      }
      setSuggestions((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: action } : x)));
      if (action === "accept" || action === "modify") {
        const data = await chapterApi.get(pid, current.id);
        setCurrent(data.chapter);
        setDraft(data.chapter.working_text);
      }
      setModifyId(null);
      toast.success({ accept: "Correction appliquée", refuse: "Suggestion refusée", modify: "Texte modifié", volontaire: "Marquée volontaire" }[action]);
    } catch (e) { toast.error("Action impossible"); }
  };

  const saveText = async () => {
    await chapterApi.update(pid, current.id, { working_text: draft });
    setCurrent((c) => ({ ...c, working_text: draft }));
    setEditMode(false);
    toast.success("Chapitre enregistré");
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    await chapterApi.reanalyze(pid, current.id);
    toast.info("Réanalyse en cours…");
    pollRef.current = setInterval(async () => {
      const st = await analysisApi.status(pid);
      if (st.status !== "running") {
        clearInterval(pollRef.current);
        setReanalyzing(false);
        loadChapter(current.id);
        loadChapters();
        toast.success("Réanalyse terminée");
      }
    }, 3000);
  };

  const autoCorrect = async () => {
    if (!current?.id) return;
    if (!window.confirm("Appliquer automatiquement toutes les fautes CERTAINES (orthographe, grammaire, ponctuation) de ce chapitre ? Le texte original reste conservé.")) return;
    try {
      const res = await chapterApi.autoCorrect(pid, current.id);
      await loadChapter(current.id);
      loadChapters();
      toast.success(`${res.applied} correction(s) appliquée(s)${res.skipped ? `, ${res.skipped} à vérifier` : ""}`);
    } catch (e) { toast.error("Correction automatique impossible"); }
  };
  useEffect(() => () => clearInterval(pollRef.current), []);

  const openCompare = async () => {
    const c = await chapterApi.compare(pid, current.id);
    setCompare(c);
    setCompareOpen(true);
  };

  // all pending excerpts to highlight inline, colour-coded by severity
  const marks = useMemo(() => {
    const m = suggestions
      .filter((s) => s.status === "pending" && typeof s.excerpt === "string" && s.excerpt.length > 0)
      .map((s) => ({ text: s.excerpt, color: (SEV[s.severity] || SEV.suggestion).color, id: s.id }));
    // longer excerpts first so they win when overlapping
    return m.sort((a, b) => b.text.length - a.text.length);
  }, [suggestions]);

  const buildParagraph = (paraText) => {
    if (marks.length === 0) return paraText;
    const nodes = [];
    let i = 0;
    let key = 0;
    while (i < paraText.length) {
      let best = null;
      for (const mk of marks) {
        const idx = paraText.indexOf(mk.text, i);
        if (idx !== -1 && (best === null || idx < best.idx || (idx === best.idx && mk.text.length > best.mk.text.length))) {
          best = { idx, mk };
        }
      }
      if (!best) { nodes.push(paraText.slice(i)); break; }
      if (best.idx > i) nodes.push(paraText.slice(i, best.idx));
      const isActive = active === best.mk.text;
      nodes.push(
        <mark
          key={key++}
          ref={isActive ? highlightRef : null}
          data-testid={`text-mark-${best.mk.id}`}
          onClick={() => setActive(best.mk.text)}
          title="Passage signalé — cliquez pour voir la remarque"
          style={{
            backgroundColor: `${best.mk.color}${isActive ? "40" : "22"}`,
            borderBottom: `2px solid ${best.mk.color}`,
            borderRadius: 2,
            padding: "0 1px",
            cursor: "pointer",
            transition: "background-color 0.25s ease",
          }}
        >
          {best.mk.text}
        </mark>
      );
      i = best.idx + best.mk.text.length;
    }
    return nodes;
  };

  // render manuscript with all excerpts highlighted
  const renderText = () => {
    const text = current?.working_text || "";
    const paras = text.split("\n").filter((p) => p.trim());
    if (paras.length === 0)
      return <p className="text-muted-foreground italic text-base" data-testid="editor-empty">Ce chapitre ne contient pas de texte (page de titre ou séparateur).</p>;
    return paras.map((p, i) => <p key={i}>{buildParagraph(p)}</p>);
  };

  const idx = chapters.findIndex((c) => c.id === cid);

  return (
    <div className="h-screen w-full flex overflow-hidden bg-background">
      {/* LEFT: chapters */}
      <aside className={`${mobileView === "chapters" ? "flex" : "hidden"} lg:flex w-full lg:w-[260px] flex-shrink-0 border-r border-border bg-surface flex-col h-full`}>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <button onClick={() => navigate(`/project/${pid}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="editor-back">
            <ArrowLeft size={16} /> Projet
          </button>
          <Brand size={18} onClick={() => navigate("/")} />
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll p-2 pb-20 lg:pb-2">
          {chapters.map((c) => (
            <button key={c.id} data-testid={`editor-chapter-${c.index}`}
              onClick={() => { navigate(`/project/${pid}/editor/${c.id}`); setMobileView("text"); }}
              className={`w-full text-left px-3 py-2.5 rounded-sm mb-0.5 transition-colors flex items-center gap-2 ${c.id === cid ? "bg-primary/10 text-primary" : "hover:bg-surface-hover text-foreground"}`}>
              <span className="font-mono text-xs text-muted-foreground w-6">{c.index + 1}</span>
              <span className="flex-1 truncate font-serif text-base">{c.title}</span>
              {c.problem_count > 0 && <span className="text-xs font-mono text-muted-foreground">{c.problem_count}</span>}
              <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${c.analysis_status === "done" ? "bg-emerald-500" : c.analysis_status === "processing" ? "bg-amber-500 animate-pulse" : "bg-border"}`} />
            </button>
          ))}
        </div>
      </aside>

      {/* CENTER: manuscript */}
      <section className={`${mobileView === "text" ? "flex" : "hidden"} lg:flex flex-1 flex-col h-full min-w-0`}>
        <div className="h-14 border-b border-border flex items-center justify-between px-3 sm:px-6 flex-shrink-0 bg-surface/60 gap-2">
          <div className="min-w-0">
            <p className="overline text-muted-foreground">Chapitre {(current?.index ?? 0) + 1}{chapters.length ? ` / ${chapters.length}` : ""}</p>
            <h1 className="font-serif text-base sm:text-lg truncate">{current?.title}</h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button onClick={() => setSizeIdx((i) => (i + 1) % 3)} title="Taille du texte" className="h-9 w-9 grid place-items-center rounded-sm border border-border hover:bg-surface-hover" data-testid="editor-text-size"><TextAa size={18} /></button>
            <Button size="sm" variant="outline" onClick={openCompare} className="rounded-sm gap-1.5" data-testid="editor-compare"><GitDiff size={15} /> <span className="hidden sm:inline">Comparer</span></Button>
            {editMode ? (
              <Button size="sm" onClick={saveText} className="rounded-sm gap-1.5" data-testid="editor-save"><FloppyDisk size={15} /> <span className="hidden sm:inline">Enregistrer</span></Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="rounded-sm gap-1.5" data-testid="editor-edit-toggle"><PencilLine size={15} /> <span className="hidden sm:inline">Modifier</span></Button>
            )}
          </div>
        </div>
        <div ref={centerRef} className="flex-1 overflow-y-auto thin-scroll px-4 sm:px-8 lg:px-16 py-6 sm:py-12 pb-24 lg:pb-12 flex justify-center scroll-smooth">
          <div className="w-full max-w-[65ch]">
            {!loading && !editMode && marks.length > 0 && (
              <div className="flex flex-wrap items-center gap-4 mb-8 pb-4 border-b border-border text-xs text-muted-foreground" data-testid="editor-legend">
                <span className="overline">Passages signalés :</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#DC262622", borderBottom: "2px solid #DC2626" }} /> Correction</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#F9731622", borderBottom: "2px solid #F97316" }} /> Suggestion</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#EAB30822", borderBottom: "2px solid #EAB308" }} /> Répétition</span>
              </div>
            )}
            {loading ? <p className="text-muted-foreground">Chargement…</p> : editMode ? (
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-[70vh] font-serif text-lg leading-relaxed resize-none" data-testid="editor-textarea" />
            ) : (
              <div className={`manuscript-text ${TEXT_SIZES[sizeIdx]}`} data-testid="editor-manuscript-area">{renderText()}</div>
            )}
          </div>
        </div>
      </section>

      {/* RIGHT: analysis */}
      <aside className={`${mobileView === "analysis" ? "flex" : "hidden"} lg:flex w-full lg:w-[380px] flex-shrink-0 border-l border-border bg-surface flex-col h-full`}>
        <div className="p-4 border-b border-border flex items-center justify-between gap-2">
          <h2 className="font-serif text-lg">Analyse EDITORIX</h2>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={autoCorrect} disabled={counts.erreur === 0} className="rounded-sm gap-1.5 text-xs" data-testid="editor-autocorrect" title="Appliquer toutes les fautes certaines">
              <Check size={14} /> Corriger les erreurs
            </Button>
            <Button size="sm" variant="ghost" onClick={reanalyze} disabled={reanalyzing} className="rounded-sm gap-1.5 text-xs" data-testid="editor-reanalyze">
              {reanalyzing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} <span className="hidden sm:inline">Réanalyser</span>
            </Button>
          </div>
        </div>

        {/* counts */}
        <div className="grid grid-cols-4 gap-2 p-3 border-b border-border text-center">
          {[["erreur", "Corrections", counts.erreur, "#DC2626"], ["suggestion", "Suggestions", counts.suggestion, "#F97316"], ["repetition", "Répétitions", counts.repetition, "#EAB308"], ["incoherence", "Incohér.", counts.incoherence, "#4F46E5"]].map(([k, label, n, color]) => (
            <div key={k} data-testid={`count-${k}`} className="rounded-sm py-2 border-t-2" style={{ borderTopColor: color, backgroundColor: `${color}0f` }}>
              <div className="font-serif text-2xl leading-none" style={{ color }}>{n}</div>
              <div className="text-[10px] uppercase tracking-wide mt-1" style={{ color }}>{label}</div>
            </div>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-wrap gap-1.5 p-3 border-b border-border">
          {[["all", "Tout"], ["erreur", "Erreurs"], ["suggestion", "Suggestions"], ["repetition", "Répétitions"], ["style", "Style"], ["grammaire", "Grammaire"], ["orthographe", "Orthographe"], ["typographie", "Typo"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-surface-hover"}`}>{l}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-3 pb-24 lg:pb-3 space-y-2.5">
          {/* inconsistencies first */}
          {filter === "all" && inconsistencies.filter((i) => i.status === "pending").map((it) => (
            <div key={it.id} className="border rounded-sm p-3 border-l-4" style={{ borderLeftColor: "#4F46E5" }} data-testid={`editor-inc-${it.id}`}>
              <div className="flex items-center gap-1.5 mb-1"><Warning size={15} style={{ color: "#4F46E5" }} /><span className="overline" style={{ color: "#4F46E5" }}>Incohérence possible</span></div>
              <p className="text-xs whitespace-pre-wrap text-muted-foreground">{T(it.description)}</p>
            </div>
          ))}

          {filtered.length === 0 && inconsistencies.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-12">
              {current?.analysis_status === "done" ? "Aucune remarque restante sur ce chapitre." : "Chapitre non analysé. Lancez l'analyse ou réanalysez ce chapitre."}
            </div>
          ) : null}

          <AnimatePresence>
            {filtered.map((s) => {
              const sev = SEV[s.severity] || SEV.suggestion;
              return (
                <motion.div key={s.id} layout initial={{ opacity: 1 }} exit={{ opacity: 0, x: 40, height: 0 }}
                  data-testid={`suggestion-${s.id}`}
                  onClick={() => setActive(typeof s.excerpt === "string" ? s.excerpt : null)}
                  className="border rounded-sm bg-card cursor-pointer overflow-hidden">
                  <div className="h-0.5" style={{ background: sev.color }} />
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="overline" style={{ color: sev.color }}>{T(s.type)}</span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{sev.label}</span>
                    </div>
                    {s.excerpt && <p className="text-sm font-serif italic mb-1.5 line-clamp-2">« {T(s.excerpt)} »</p>}
                    {s.suggestion && s.type !== "repetition" && (
                      <p className="text-sm mb-1.5"><span className="text-muted-foreground">→ </span><span style={{ color: sev.color }}>{T(s.suggestion)}</span></p>
                    )}
                    {s.suggestion && s.type === "repetition" && (
                      <p className="text-sm mb-1.5"><span className="text-muted-foreground">Propositions : </span><span style={{ color: sev.color }}>{T(s.suggestion)}</span></p>
                    )}
                    {s.explanation && <p className="text-xs text-muted-foreground mb-2">{T(s.explanation)}</p>}
                    {s.matched === false && (
                      <p className="text-[11px] mb-2 flex items-start gap-1" style={{ color: "#B45309" }} data-testid={`unmatched-${s.id}`}>
                        <Warning size={13} className="mt-0.5 flex-shrink-0" /> Passage non localisé automatiquement — cliquez « Modifier » pour l'ajuster.
                      </p>
                    )}

                    {modifyId === s.id ? (
                      <div className="flex items-center gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
                        <Input value={modifyText} onChange={(e) => setModifyText(e.target.value)} className="h-8 text-sm" autoFocus data-testid={`modify-input-${s.id}`} />
                        <button onClick={() => act(s, "modify", modifyText)} className="text-primary" data-testid={`modify-confirm-${s.id}`}><Check size={18} /></button>
                        <button onClick={() => setModifyId(null)} className="text-muted-foreground"><X size={18} /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
                        {s.suggestion && s.type !== "repetition" && (
                          <button onClick={() => act(s, "accept")} data-testid={`accept-${s.id}`} className="flex-1 h-7 rounded-sm bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 text-xs font-medium flex items-center justify-center gap-1 transition-colors"><Check size={13} /> Accepter</button>
                        )}
                        <button onClick={() => { setModifyId(s.id); setModifyText(T(s.suggestion) || T(s.excerpt)); }} data-testid={`modify-${s.id}`} className="flex-1 h-7 rounded-sm bg-secondary hover:bg-surface-hover text-xs font-medium flex items-center justify-center gap-1 transition-colors"><PencilSimple size={13} /> Modifier</button>
                        <button onClick={() => act(s, s.type === "repetition" ? "volontaire" : "refuse")} data-testid={`refuse-${s.id}`} className="flex-1 h-7 rounded-sm bg-secondary hover:bg-surface-hover text-xs font-medium flex items-center justify-center gap-1 transition-colors"><X size={13} /> {s.type === "repetition" ? "Volontaire" : "Refuser"}</button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </aside>

      {/* MOBILE tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 h-16 bg-surface border-t border-border grid grid-cols-3 z-40" data-testid="editor-mobile-tabs">
        {[
          ["chapters", "Chapitres", ListBullets, chapters.length],
          ["text", "Texte", Article, null],
          ["analysis", "Analyse", ChatText, suggestions.filter((s) => s.status === "pending").length],
        ].map(([key, label, Icon, badge]) => (
          <button key={key} onClick={() => setMobileView(key)} data-testid={`mobile-tab-${key}`}
            className={`flex flex-col items-center justify-center gap-0.5 text-xs relative ${mobileView === key ? "text-primary" : "text-muted-foreground"}`}>
            <Icon size={20} weight={mobileView === key ? "fill" : "regular"} />
            {label}
            {badge ? <span className="absolute top-1.5 right-1/2 translate-x-4 text-[9px] font-mono bg-primary text-primary-foreground rounded-full px-1">{badge}</span> : null}
          </button>
        ))}
      </nav>

      {/* compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-popover max-w-4xl" data-testid="compare-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl sm:text-2xl">Comparateur — {compare?.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto thin-scroll">
            <div>
              <p className="overline text-muted-foreground mb-2">Version originale</p>
              <div className="manuscript-text text-sm border border-border rounded-sm p-4 bg-background whitespace-pre-wrap">{compare?.original}</div>
            </div>
            <div>
              <p className="overline text-primary mb-2">Version actuelle</p>
              <div className="manuscript-text text-sm border border-primary/30 rounded-sm p-4 bg-primary/5 whitespace-pre-wrap">{compare?.current}</div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" className="rounded-sm gap-1.5" data-testid="restore-original"
              onClick={async () => { await chapterApi.restore(pid, current.id); toast.success("Version originale restaurée"); setCompareOpen(false); loadChapter(current.id); }}>
              Restaurer l'original
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
